import { useEffect, useRef, useState } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { withLang, type SiteLang } from "@/lib/lang-url";

/** Each language named in itself. The first three have the whole site; the rest start with the home page. */
export const LANGS: Array<{ code: SiteLang; name: string; full?: boolean }> = [
  { code: "en", name: "English", full: true },
  { code: "pt", name: "Português", full: true },
  { code: "es", name: "Español", full: true },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "ja", name: "日本語" },
  { code: "he", name: "עברית" },
  { code: "ar", name: "العربية" },
  { code: "zh", name: "中文" },
  { code: "hi", name: "हिन्दी" },
  { code: "ru", name: "Русский" },
  { code: "sw", name: "Kiswahili" },
  { code: "yo", name: "Yorùbá" },
  { code: "ha", name: "Hausa" },
  { code: "ig", name: "Igbo" },
  { code: "pcm", name: "Naijá" },
  { code: "mr", name: "मराठी" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" },
  { code: "tr", name: "Türkçe" },
  { code: "is", name: "Íslenska" },
  { code: "sv", name: "Svenska" },
  { code: "it", name: "Italiano" },
  { code: "pl", name: "Polski" },
  { code: "ro", name: "Română" },
  { code: "nl", name: "Nederlands" },
];

/**
 * Discreet language picker: one button showing the current language,
 * opening the full list. `direction` controls where the panel opens.
 */
export function LanguageMenu({
  tone = "bone",
  direction = "down",
  bordered = false,
}: {
  tone?: "bone" | "ink";
  direction?: "up" | "down";
  bordered?: boolean;
}) {
  const { tx } = useI18n();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = router.$lang.get();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const currentLang = LANGS.find((l) => l.code === current) ?? { code: "en" as SiteLang, name: "English" };
  const base = tone === "ink" ? "text-ink" : "text-bone";
  const label = tx({ en: "Language", pt: "Idioma", es: "Idioma" });

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        className={
          bordered
            ? `inline-flex h-11 items-center gap-2 border border-bone/15 px-4 font-sans text-[12px] font-light text-bone/70 transition-colors hover:border-bone/30 hover:text-bone`
            : `inline-flex h-11 items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] ${base}/60 transition-opacity hover:opacity-100 md:text-[10px]`
        }
      >
        <svg viewBox="0 0 24 24" width={bordered ? 14 : 12} height={bordered ? 14 : 12} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
        </svg>
        <span lang={currentLang.code}>{currentLang.name}</span>
        <svg viewBox="0 0 24 24" width={bordered ? 10 : 8} height={bordered ? 10 : 8} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          className={`absolute z-[60] w-52 border border-bone/12 bg-ink/95 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.8)] backdrop-blur-md animate-in fade-in slide-in-from-top-1 duration-200 ${
            direction === "up" ? "bottom-full mb-3" : "top-full mt-3"
          } ${bordered ? "left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0" : "right-0"}`}
        >
          <p className="border-b border-bone/10 px-4 pb-2 pt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-bone/40">
            {label}
          </p>
          <ul
            role="listbox"
            aria-label={label}
            className="lang-scroll max-h-64 overflow-y-auto py-1"
          >
            {LANGS.map((l) => (
              <li key={l.code} role="option" aria-selected={current === l.code}>
                <a
                  href={withLang(l.full ? pathname : "/", l.code)}
                  lang={l.code}
                  hrefLang={l.code}
                  aria-current={current === l.code ? "true" : undefined}
                  onClick={() => setOpen(false)}
                  className={`group flex items-center justify-between px-4 py-2.5 font-sans text-[12px] font-light transition-colors hover:bg-bone/8 hover:text-bone ${current === l.code ? "text-bone" : "text-bone/60"}`}
                >
                  <span lang={l.code}>{l.name}</span>
                  {current === l.code ? (
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M4 12.5l5 5L20 6.5" />
                    </svg>
                  ) : null}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
